import { useEffect, useSyncExternalStore } from "react";
import type { ApiClient } from "../../api/client";
import { callerIdDevice, type CallerIdStatus, type DeviceLine } from "./caller-id";

/**
 * Listens to the caller-ID device of this till pc while the till is open. Every number goes to the
 * server; the server shows the call on every till (also this one) and ignores the same ring twice. The
 * way of listening that gives numbers is shared with the shop's other till pcs.
 */
export function useCallerIdDevice(api: ApiClient, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    void callerIdDevice.start({
      report: (digits) => {
        api
          .request("POST", "/device/calls", { body: { phone: digits, source: "usb" }, retry: true })
          .catch(() => undefined);
      },
      // a device on another till pc of the shop starts with the way that already works here (Pita
      // Antalya, 11/10/2026: a line on each till pc)
      way: async () => (await api.request<{ country: number | null }>("GET", "/device/calls/way")).country,
      proven: (country) => {
        api.request("PUT", "/device/calls/way", { body: { country }, retry: true }).catch(() => undefined);
      },
    });
    return () => {
      void callerIdDevice.stop();
    };
  }, [api, enabled]);
}

/** Whether the device of this till is connected (Beheer › Printers & toestellen). */
export function useCallerIdStatus(): CallerIdStatus {
  return useSyncExternalStore(callerIdDevice.subscribe, callerIdDevice.getStatus);
}

/** What the device of this till wrote and was asked, the last lines (Beheer › Printers & toestellen). */
export function useCallerIdLog(): readonly DeviceLine[] {
  return useSyncExternalStore(callerIdDevice.subscribe, callerIdDevice.getLog);
}
